/**
 * Parent portal — list of children + child summary (marks/progress/photos/fees).
 *
 * Routes:
 *   /app                  → ParentHome (this file's default export)
 *   /app/messages          → internal mail (shared with other roles)
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Student, ChartLineUp, Books, Receipt, Images, DownloadSimple, ChatCircleDots } from "@phosphor-icons/react";
import { ParentChat } from "@/components/ParentChat";
import { OfflineBanner } from "@/components/OfflineBanner";
import { FeeBreakdownButton } from "@/components/FeeBreakdownDialog";
import { ParentLeaveCard } from "@/components/ParentLeaveCard";
import { ParentMessMenuCard } from "@/components/ParentMessMenuCard";
import { MessQrCard } from "@/components/MessQrCard";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const API_BASE = API;
const downloadReceipt = (feeId) => {
  window.open(`${API_BASE}/fees/${feeId}/receipt`, "_blank", "noopener");
};

export default function ParentHome() {
  const [children, setChildren] = useState([]);
  const [active, setActive] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/parent/children").then((r) => {
      setChildren(r.data || []);
      if (r.data?.[0]) setActive(r.data[0].id);
    });
  }, []);
  useEffect(() => {
    if (!active || active === "__chat") return;
    api.get(`/parent/children/${active}/summary`).then((r) => setSummary(r.data));
  }, [active]);

  const payFee = async (feeId) => {
    if (!window.confirm("Continue to secure payment via Stripe?")) return;
    try {
      const res = await api.post(`/parent/fees/${feeId}/checkout`, { origin_url: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="My family" subtitle="See your child's progress, marks and fees. Pay online from here.">
      {children.length === 0 ? (
        <>
          <OfflineBanner />
          <div className="vs-card p-8 text-center text-[var(--vs-text-secondary)]">
            No children linked to your account yet. Ask your school/hostel admin to link your account.
          </div>
        </>
      ) : (
        <>
          <OfflineBanner />
          <Tabs value={active || ""} onValueChange={setActive}>
          <TabsList>
            {children.map((c) => (
              <TabsTrigger key={c.id} value={c.id} data-testid={`parent-child-${c.vs_id}`}>
                <Student size={12} className="mr-1.5" /> {c.name} <span className="ml-2 font-mono text-[10px] opacity-60">({c.vs_id})</span>
              </TabsTrigger>
            ))}
            <TabsTrigger value="__chat" data-testid="parent-tab-chat">
              <ChatCircleDots size={12} className="mr-1.5" /> Ask Vidya
            </TabsTrigger>
          </TabsList>
          <TabsContent value="__chat">
            <div className="mt-4">
              <ParentChat />
            </div>
          </TabsContent>
          {children.map((c) => (
            <TabsContent key={c.id} value={c.id}>
              {summary && summary.student?.id === c.id ? (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="vs-card p-4"><div className="eyebrow">Class</div><div className="font-display text-2xl mt-1">{c.class_name || "—"}{c.section ? `-${c.section}` : ""}</div></div>
                    <div className="vs-card p-4"><div className="eyebrow">APAAR id</div><div className="font-mono text-sm mt-1">{c.apaar_id || "—"}</div></div>
                    <div className="vs-card p-4"><div className="eyebrow">Marksheets</div><div className="font-display text-2xl mt-1">{summary.marksheets?.length || 0}</div></div>
                    <div className="vs-card p-4"><div className="eyebrow">Photos</div><div className="font-display text-2xl mt-1">{summary.photos?.length || 0}</div></div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="vs-card p-5">
                      <div className="eyebrow flex items-center gap-1.5 mb-2"><Books size={12} /> Latest marksheets</div>
                      <ul className="text-sm space-y-2" data-testid="parent-marksheets">
                        {summary.marksheets?.slice(0, 5).map((m) => (
                          <li key={m.id} className="flex items-center justify-between p-2 rounded bg-[var(--vs-bg-subtle)]">
                            <span>{m.exam_name} {m.academic_year ? `(${m.academic_year})` : ""}</span>
                            <span className="font-mono"><Badge variant="outline" className="mr-2">{m.grade || "—"}</Badge>{m.percentage != null ? `${m.percentage}%` : ""}</span>
                          </li>
                        ))}
                        {(summary.marksheets?.length || 0) === 0 && <li className="text-[var(--vs-text-secondary)]">No published marksheets yet.</li>}
                      </ul>
                    </div>

                    <div className="vs-card p-5">
                      <div className="eyebrow flex items-center gap-1.5 mb-2"><ChartLineUp size={12} /> Progress reports</div>
                      <ul className="text-sm space-y-2">
                        {summary.progress?.slice(0, 5).map((p) => (
                          <li key={p.id} className="p-2 rounded bg-[var(--vs-bg-subtle)] text-xs">
                            <div className="font-mono">{p.period}</div>
                            <div className="text-[var(--vs-text-secondary)]">Academic {p.academic} · Discipline {p.discipline} · Sports {p.sports} · Social {p.social}</div>
                          </li>
                        ))}
                        {(summary.progress?.length || 0) === 0 && <li className="text-[var(--vs-text-secondary)]">No progress reports yet.</li>}
                      </ul>
                    </div>

                    <div className="vs-card p-5 lg:col-span-2">
                      <div className="eyebrow flex items-center gap-1.5 mb-2"><Receipt size={12} /> Fees</div>
                      <table className="w-full text-sm">
                        <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]"><th className="px-3 py-2">Period</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Receipt</th><th className="px-3 py-2 text-right"></th></tr></thead>
                        <tbody>
                          {summary.fees?.map((f) => (
                            <tr key={f.id} className="border-t border-[var(--vs-border)]" data-testid={`parent-fee-${f.id}`}>
                              <td className="px-3 py-2 font-mono text-xs">{f.period}</td>
                              <td className="px-3 py-2 font-mono">{fmt(f.amount)}</td>
                              <td className="px-3 py-2"><Badge variant="outline" className={f.status === "paid" ? "border-[var(--vs-success)]/40 text-[var(--vs-success)]" : "border-[var(--vs-warning)]/40 text-[var(--vs-warning)]"}>{f.status}</Badge></td>
                              <td className="px-3 py-2 text-xs font-mono text-right">{f.receipt_no || "—"}</td>
                              <td className="px-3 py-2 text-right">
                                {f.status === "paid" || f.status === "sponsored" ? (
                                  <Button size="sm" variant="outline" onClick={() => downloadReceipt(f.id)} data-testid={`parent-receipt-${f.id}`}>
                                    <DownloadSimple size={12} className="mr-1" /> Receipt
                                  </Button>
                                ) : (
                                  <div className="flex gap-1 justify-end">
                                    <FeeBreakdownButton fee={f} onPay={payFee} />
                                    <Button size="sm" onClick={() => payFee(f.id)} data-testid={`parent-pay-${f.id}`} className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">Pay {fmt((f.amount || 0) - (f.paid || 0))}</Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                          {(summary.fees?.length || 0) === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--vs-text-secondary)]">No fees yet.</td></tr>}
                        </tbody>
                      </table>
                    </div>

                    <ParentLeaveCard studentId={c.id} studentName={c.name} />
                    <div className="vs-card p-5 lg:col-span-2 flex items-center justify-between gap-3">
                      <div>
                        <div className="eyebrow mb-1">Mess swipe-in</div>
                        <div className="text-sm">Download {c.name}'s QR card. The mess warden scans it at every meal — fastest way to log mess attendance.</div>
                      </div>
                      <MessQrCard studentId={c.id} studentName={c.name} />
                    </div>
                    <ParentMessMenuCard studentId={c.id} />

                    <div className="vs-card p-5 lg:col-span-2">
                      <div className="eyebrow flex items-center gap-1.5 mb-2"><Images size={12} /> Recent photos</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {summary.photos?.map((p) => (
                          <div key={p.id} className="aspect-square rounded-md border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)] grid place-items-center font-mono text-[9px] text-[var(--vs-text-secondary)] p-2 text-center break-all">{(p.storage_path || "").slice(-32)}</div>
                        ))}
                        {(summary.photos?.length || 0) === 0 && <div className="col-span-full text-center text-[var(--vs-text-secondary)] py-6">No photos uploaded yet.</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ) : <div className="text-sm text-[var(--vs-text-secondary)] mt-4">Loading…</div>}
            </TabsContent>
          ))}
          </Tabs>
        </>
      )}
    </DashboardLayout>
  );
}
